import {MutableRefObject, useCallback, useMemo, useRef} from 'react'
import {
  ListHandlerRunUseCaseListeners,
  ListHandlerStatus,
  ListHandlerUseCase,
  ListHandlerUseCaseConfig,
  ListHandlerUseCaseFunction,
  ListHandlerUseCaseStatus,
} from 'types/components'

export function useListHandler<T, K extends string, M>(
  defaultUseCases?: ListHandlerUseCase<T, K, M>[],
) {
  const useCases = useRef<ListHandlerUseCase<T, K, M>[]>(defaultUseCases || [])
  const isRunning = useRef(false)

  const handleSetUseCaseConfig = useCallback(
    (name: K, config: ListHandlerUseCaseConfig) => {
      for (const [property, value] of Object.entries(config)) {
        for (const useCase of useCases.current) {
          if (useCase.name === name) {
            useCase[property] = value
          }
        }
      }
    },
    [],
  )

  const handleRegisterUseCase = useCallback(
    (
      name: K,
      fn: ListHandlerUseCaseFunction<T, M>,
      disabled: boolean = false,
    ) => {
      useCases.current.push({name, fn, disabled})
    },
    [],
  )

  const handleRunUseCases = useCallback(
    async (list: T[], listeners?: ListHandlerRunUseCaseListeners<T, K, M>) => {
      if (isRunning.current || !list) return
      isRunning.current = true

      const {onStart, onStartItem, onStartUseCase, onLoadEndItem, onResult} =
        listeners || {}

      const total = list.length
      let totalSuccess = 0
      let totalFailed = 0
      let totalWarning = 0

      const listSuccess: T[] = []
      const listFailed: T[] = []
      const listWarning: T[] = []

      const statuses: ListHandlerStatus[] = Array.from(
        {length: total},
        () => null,
      )

      if (onStart)
        onStart({total, totalSuccess, totalFailed, totalWarning, statuses})

      for (let index = 0; index < total; index += 1) {
        const item = list[index]
        const meta: MutableRefObject<M | null> = {current: null}
        let status: ListHandlerUseCaseStatus = 'success'

        if (onStartItem)
          onStartItem({
            item,
            index,
            meta,
            total,
            totalSuccess,
            totalFailed,
            totalWarning,
            statuses,
          })

        for (const useCase of useCases.current) {
          if (status !== 'success') break
          const {name, fn} = useCase

          if (onStartUseCase)
            onStartUseCase({
              name,
              item,
              index,
              total,
              totalSuccess,
              totalFailed,
              totalWarning,
              statuses,
            })

          // eslint-disable-next-line no-await-in-loop, react-hooks/rules-of-hooks
          const resultStatus = await fn(item, meta)

          if (typeof resultStatus === 'boolean') {
            status = resultStatus ? 'success' : 'failed'
          } else {
            status = resultStatus
          }
        }

        if (status === 'success') {
          totalSuccess += 1
          listSuccess.push(item)
        } else if (status === 'warning') {
          totalWarning += 1
          listWarning.push(item)
        } else {
          totalFailed += 1
          listFailed.push(item)
        }

        statuses[index] = status

        if (onLoadEndItem)
          onLoadEndItem({
            item,
            index,
            meta,
            status,
            total,
            totalSuccess,
            totalFailed,
            totalWarning,
            statuses,
          })
      }

      if (onResult)
        onResult({
          total,
          totalSuccess,
          totalFailed,
          totalWarning,
          statuses,
          listSuccess,
          listFailed,
          listWarning,
        })

      isRunning.current = false
    },
    [],
  )

  return useMemo(
    () => ({
      registerUseCase: handleRegisterUseCase,
      runUseCases: handleRunUseCases,
      setUseCaseConfig: handleSetUseCaseConfig,
    }),
    [handleRegisterUseCase, handleRunUseCases, handleSetUseCaseConfig],
  )
}
